import React, { useEffect, useState } from "react";
import { Drawer, Table, Typography } from "antd";
import { CoverageMonthRow, CoverageRow, fetchCoverage, fetchCoverageMonths } from "./api";

const { Paragraph } = Typography;

/** What the raw airing table holds, per platform and channel: a check before uploading. */
const CoverageTab: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [rows, setRows] = useState<CoverageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<CoverageRow | null>(null);
  const [months, setMonths] = useState<CoverageMonthRow[]>([]);
  const [monthsLoading, setMonthsLoading] = useState(false);

  const openMonths = (row: CoverageRow) => {
    setSelected(row);
    setMonthsLoading(true);
    fetchCoverageMonths(row.platform, row.channel)
      .then((data) => setMonths(data.rows))
      .finally(() => setMonthsLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    fetchCoverage()
      .then((data) => setRows(data.rows))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  return (
    <div style={{ padding: 24 }}>
      <Paragraph>
        Airing rows loaded so far, by platform and channel. A platform that stops at an older last day is
        missing recent weeks; a month you expect but do not see here has not been uploaded. Click a row to
        see it month by month.
      </Paragraph>
      <Table
        size="small"
        rowKey={(r) => `${r.platform}|${r.channel}`}
        loading={loading}
        dataSource={rows}
        pagination={false}
        onRow={(row) => ({ onClick: () => openMonths(row), style: { cursor: "pointer" } })}
        columns={[
          { title: "Platform", dataIndex: "platform", sorter: (a, b) => a.platform.localeCompare(b.platform) },
          { title: "Channel", dataIndex: "channel", sorter: (a, b) => a.channel.localeCompare(b.channel) },
          { title: "First day", dataIndex: "firstDay" },
          { title: "Last day", dataIndex: "lastDay", sorter: (a, b) => a.lastDay.localeCompare(b.lastDay) },
          { title: "Rows", dataIndex: "rows", align: "right", render: (v: number) => v.toLocaleString(), sorter: (a, b) => a.rows - b.rows },
          { title: "Last loaded", dataIndex: "lastLoadedAt", render: (v: string | null) => (v ? new Date(v).toLocaleString() : "") },
        ]}
      />

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} width={920} title={selected ? `${selected.platform} / ${selected.channel}, by month` : ""}>
        <Table
          size="small"
          rowKey="month"
          loading={monthsLoading}
          dataSource={months}
          pagination={false}
          scroll={{ x: 860 }}
          columns={[
            { title: "Month", dataIndex: "month", width: 100 },
            { title: "Days with data", dataIndex: "days", width: 130, align: "right" },
            { title: "First day", dataIndex: "firstDay", width: 120 },
            { title: "Last day", dataIndex: "lastDay", width: 120 },
            { title: "Rows", dataIndex: "rows", width: 100, align: "right", render: (v: number) => v.toLocaleString() },
            { title: "Minutes watched", dataIndex: "minutes", width: 150, align: "right", render: (v: number) => v.toLocaleString() },
            { title: "Last loaded", dataIndex: "lastLoadedAt", width: 130, render: (v: string | null) => (v ? new Date(v).toLocaleDateString() : "") },
          ]}
        />
      </Drawer>
    </div>
  );
};

export default CoverageTab;
